import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import ProductMockup from "./ProductMockup";

const ease = [0.22, 1, 0.36, 1] as const;

// 5-stage clinical workflow timeline
type Stage = 0 | 1 | 2 | 3 | 4;
const STAGES = [
  { key: "prioritize", label: "Prioritize" },
  { key: "review", label: "Review" },
  { key: "image", label: "Image" },
  { key: "document", label: "Document" },
  { key: "route", label: "Route" },
] as const;

// Stage durations - approximately 9s total walkthrough
const STAGE_DELAYS = [0, 1800, 3800, 6200, 8600];

type WorklistRow = {
  id: string;
  patient: string;
  study: string;
  status: "STAT" | "ROUTINE" | "AI · PRIORITY" | "REVIEW" | "PENDING";
  priority?: boolean;
  order: { initial: number; afterTriage: number };
};

const initialWorklist: WorklistRow[] = [
  { id: "001", patient: "M. Keller · 54", study: "Discharge summary", status: "REVIEW", order: { initial: 0, afterTriage: 1 } },
  { id: "002", patient: "A. Lange · 67", study: "Medication reconciliation", status: "ROUTINE", order: { initial: 1, afterTriage: 2 } },
  { id: "003", patient: "S. Reinhardt · 41", study: "Sepsis risk · ED", status: "AI · PRIORITY", priority: true, order: { initial: 3, afterTriage: 0 } },
  { id: "004", patient: "T. Brand · 72", study: "Stroke follow-up", status: "STAT", order: { initial: 2, afterTriage: 3 } },
  { id: "005", patient: "J. Patel · 38", study: "Care gap review", status: "PENDING", order: { initial: 4, afterTriage: 4 } },
];

// Clinical assist text — decision support for clinician review (not autonomous diagnosis or treatment)
const dictation =
  "EHR summary shows ED arrival with hypotension and elevated lactate. Imaging evidence shows right lower lobe opacity. Validated sepsis pathway reference surfaced for clinician review. Recommend care team notification.";

// Patient signal chips shown in central EHR panel
const signalChips = [
  { label: "HR", value: "118" },
  { label: "BP", value: "96/58" },
  { label: "Temp", value: "39.1" },
  { label: "Lactate", value: "3.4" },
];

// Timeline events in central EHR panel — last item highlights when AI raises signal
const timelineEvents = [
  { label: "Vitals updated", aiSignal: false },
  { label: "Lab result received", aiSignal: false },
  { label: "ED note parsed", aiSignal: false },
  { label: "AI risk signal raised", aiSignal: true },
];

const DeployMockup = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.15, margin: "0px 0px -10% 0px" });
  const reduce = useReducedMotion();
  const [stage, setStage] = useState<Stage>(reduce ? 4 : 0);
  const [typedChars, setTypedChars] = useState(0);

  // Drive stage timeline
  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setStage(4);
      setTypedChars(dictation.length);
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    STAGE_DELAYS.forEach((delay, i) => {
      timers.push(setTimeout(() => setStage(i as Stage), delay));
    });
    return () => timers.forEach(clearTimeout);
  }, [inView, reduce]);

  // Typing effect during assist stage
  useEffect(() => {
    if (reduce) return;
    if (stage < 2) {
      setTypedChars(0);
      return;
    }
    if (stage >= 3) {
      setTypedChars(dictation.length);
      return;
    }
    let i = 0;
    const interval = setInterval(() => {
      i += 3;
      setTypedChars(Math.min(i, dictation.length));
      if (i >= dictation.length) clearInterval(interval);
    }, 28);
    return () => clearInterval(interval);
  }, [stage, reduce]);

  const sortedWorklist = useMemo(() => {
    const useTriaged = stage >= 1;
    return [...initialWorklist].sort((a, b) =>
      useTriaged ? a.order.afterTriage - b.order.afterTriage : a.order.initial - b.order.initial
    );
  }, [stage]);

  return (
    <ProductMockup label="clinical workflow · live" contentHeight="h-auto md:h-[420px]">
      {/* Stage indicator */}
      <div ref={ref} className="flex items-center gap-1.5 md:gap-3 border-b border-border bg-background/40 px-3 py-2 overflow-x-auto">
        {STAGES.map((s, i) => {
          const active = stage === i;
          const done = stage > i;
          return (
            <div key={s.key} className="flex items-center gap-1.5 md:gap-3 shrink-0">
              <div className="flex items-center gap-1.5">
                <motion.span
                  aria-hidden
                  className={`h-1.5 w-1.5 rounded-full ${
                    done ? "bg-primary" : active ? "bg-primary" : "bg-foreground/20"
                  }`}
                  animate={
                    active && !reduce
                      ? { scale: [1, 1.6, 1], opacity: [1, 0.6, 1] }
                      : { scale: 1, opacity: 1 }
                  }
                  transition={{ duration: 1.4, ease, repeat: active ? Infinity : 0 }}
                />
                <span
                  className={`text-[8.5px] md:text-[9px] uppercase tracking-[0.14em] ${
                    active ? "text-primary" : done ? "text-foreground/70" : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < STAGES.length - 1 && (
                <span className={`h-px w-4 md:w-6 ${done ? "bg-primary/50" : "bg-border"}`} aria-hidden />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col md:grid md:grid-cols-12 md:grid-rows-2 bg-background md:min-h-[340px] text-[10px]">
        {/* Clinical worklist (left) */}
        <div className="md:col-span-4 md:row-span-2 border-b md:border-b-0 md:border-r border-border p-3">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
              Clinical worklist
            </p>
            <AnimatePresence>
              {stage >= 1 && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease }}
                  className="text-[8px] uppercase tracking-wider text-primary"
                >
                  AI prioritized
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          <div className="space-y-1">
            {sortedWorklist.map((row, i) => (
              <motion.div
                key={row.id}
                layout={!reduce}
                initial={{ opacity: 0, x: reduce ? 0 : -4 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{
                  duration: 0.35,
                  ease,
                  delay: stage === 0 ? 0.1 + i * 0.08 : 0,
                  layout: { duration: 0.6, ease },
                }}
                className={`relative flex items-center justify-between rounded px-2 py-1.5 border ${
                  row.priority && stage >= 1
                    ? "border-primary/40 bg-primary/5"
                    : "border-transparent"
                }`}
              >
                {row.priority && stage >= 1 && (
                  <motion.span
                    aria-hidden
                    className="absolute inset-0 rounded border border-primary/60 pointer-events-none"
                    initial={{ opacity: 0 }}
                    animate={!reduce ? { opacity: [0, 0.8, 0.3] } : { opacity: 0.3 }}
                    transition={{ duration: 1.6, ease, repeat: Infinity, repeatType: "reverse" }}
                  />
                )}
                <div className="relative min-w-0 flex-1">
                  <p className="text-foreground/85 font-medium truncate whitespace-nowrap">{row.patient}</p>
                  <p className="text-muted-foreground text-[9px] truncate whitespace-nowrap">{row.study}</p>
                </div>
                <span
                  className={`relative shrink-0 ml-2 text-[8.5px] uppercase tracking-wide whitespace-nowrap ${
                    row.priority && stage >= 1
                      ? "text-primary"
                      : row.status === "STAT"
                        ? "text-foreground/80"
                        : "text-muted-foreground"
                  }`}
                >
                  {row.status}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Central panel — EHR context, embedded imaging, then clinical note */}
        <div className="md:col-span-5 md:row-span-2 border-b md:border-b-0 md:border-r border-border p-3 relative">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
              {stage === 2
                ? "Imaging opened · CXR"
                : stage >= 3
                  ? "Clinical note · Case 003"
                  : "EHR · Patient context · Case 003"}
            </p>
            <p className="text-[8px] uppercase tracking-wider text-muted-foreground">
              {stage === 2 ? "Portable CXR" : "ED · live"}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {/* Stage 0–1: EHR patient context */}
            {stage < 2 && (
              <motion.div
                key="ehr-context"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease }}
                className="relative rounded border border-border bg-foreground/[0.025] p-3 space-y-3"
              >
                {/* Patient signal chips */}
                <div className="grid grid-cols-4 gap-1.5">
                  {signalChips.map((chip, i) => (
                    <motion.div
                      key={chip.label}
                      initial={{ opacity: 0, y: reduce ? 0 : 4 }}
                      animate={inView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.4, ease, delay: 0.2 + i * 0.08 }}
                      className="rounded border border-border bg-background px-1.5 py-1 text-center"
                    >
                      <p className="text-[7.5px] uppercase tracking-wider text-muted-foreground">{chip.label}</p>
                      <p className="text-[11px] font-medium text-foreground/85 leading-tight">{chip.value}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Timeline */}
                <div className="space-y-1.5">
                  {timelineEvents.map((evt, i) => {
                    const visible = stage >= 1 || i < 2;
                    const aiActive = evt.aiSignal && stage >= 1;
                    return (
                      <motion.div
                        key={evt.label}
                        initial={{ opacity: 0, x: reduce ? 0 : -4 }}
                        animate={visible ? { opacity: 1, x: 0 } : { opacity: 0.25, x: 0 }}
                        transition={{ duration: 0.4, ease, delay: 0.3 + i * 0.15 }}
                        className="flex items-center gap-2"
                      >
                        <motion.span
                          aria-hidden
                          className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                            aiActive ? "bg-primary" : "bg-foreground/30"
                          }`}
                          animate={
                            aiActive && !reduce
                              ? { scale: [1, 1.6, 1], opacity: [1, 0.6, 1] }
                              : { scale: 1, opacity: 1 }
                          }
                          transition={{ duration: 1.6, ease, repeat: aiActive ? Infinity : 0 }}
                        />
                        <span
                          className={`text-[9.5px] ${
                            aiActive ? "text-primary" : "text-foreground/70"
                          }`}
                        >
                          {evt.label}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Highlighted AI footer */}
                {stage >= 1 && (
                  <motion.div
                    initial={{ opacity: 0, y: reduce ? 0 : 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease, delay: 0.6 }}
                    className="rounded border border-primary/40 bg-primary/5 px-2 py-1.5"
                  >
                    <p className="text-[9px] uppercase tracking-wider text-primary">
                      AI · elevated sepsis risk
                    </p>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Stage 2: embedded imaging evidence */}
            {stage === 2 && (
              <motion.div
                key="imaging"
                initial={{ opacity: 0, y: reduce ? 0 : 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduce ? 0 : -4 }}
                transition={{ duration: 0.35, ease }}
                className="relative rounded border border-border bg-foreground/[0.025] p-3 space-y-2"
              >
                <div className="relative aspect-[16/9] rounded bg-foreground/[0.04] border border-border overflow-hidden">
                  <svg viewBox="0 0 240 170" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
                    {/* Stylized frontal CXR: two lung fields with mediastinum */}
                    {/* Right lung (viewer-left = patient right) */}
                    <path
                      d="M 67 35 C 44 45, 35 76, 39 110 C 42 137, 61 151, 83 148 C 98 146, 106 133, 107 112 L 107 63 C 104 43, 88 30, 67 35 Z"
                      fill="none"
                      stroke="hsl(var(--foreground))"
                      strokeOpacity="0.28"
                      strokeWidth="1"
                    />
                    {/* Left lung (viewer-right = patient left) */}
                    <path
                      d="M 139 40 C 159 31, 181 40, 193 61 C 208 88, 205 129, 184 145 C 166 158, 140 149, 134 125 L 132 66 C 131 54, 132 45, 139 40 Z"
                      fill="none"
                      stroke="hsl(var(--foreground))"
                      strokeOpacity="0.28"
                      strokeWidth="1"
                    />
                    {/* Mediastinum / spine */}
                    <line x1="120" y1="32" x2="120" y2="146" stroke="hsl(var(--foreground))" strokeOpacity="0.18" strokeWidth="0.8" />
                    {/* Heart / mediastinal contour — subtle, viewer-right of midline */}
                    <path
                      d="M 122 86 C 135 88, 146 101, 147 120 C 146 137, 135 148, 121 150"
                      fill="none"
                      stroke="hsl(var(--foreground))"
                      strokeOpacity="0.22"
                      strokeWidth="0.9"
                    />
                    {/* Subtle clavicle arcs */}
                    <path
                      d="M 50 42 Q 85 28, 118 40"
                      fill="none"
                      stroke="hsl(var(--foreground))"
                      strokeOpacity="0.14"
                      strokeWidth="0.6"
                    />
                    <path
                      d="M 122 40 Q 155 28, 192 44"
                      fill="none"
                      stroke="hsl(var(--foreground))"
                      strokeOpacity="0.14"
                      strokeWidth="0.6"
                    />

                    {/* RLL opacity — lower viewer-left lung field */}
                    <motion.ellipse
                      cx="74"
                      cy="118"
                      rx="24"
                      ry="14"
                      fill="hsl(var(--primary))"
                      fillOpacity="0.22"
                      stroke="hsl(var(--primary))"
                      strokeWidth="1"
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={
                        !reduce
                          ? { opacity: 1, scale: [1, 1.06, 1] }
                          : { opacity: 1, scale: 1 }
                      }
                      transition={
                        !reduce
                          ? {
                              opacity: { duration: 0.4, ease, delay: 0.2 },
                              scale: { duration: 1.8, ease, delay: 0.4, repeat: Infinity, repeatType: "reverse" },
                            }
                          : { duration: 0.4, ease }
                      }
                      style={{ transformOrigin: "74px 118px" }}
                    />
                    {/* Callout */}
                    <line x1="94" y1="112" x2="108" y2="103" stroke="hsl(var(--primary))" strokeOpacity="0.7" strokeWidth="0.5" />
                    <text x="110" y="101" fontSize="7" fill="hsl(var(--primary))">RLL opacity</text>
                  </svg>
                  <span className="absolute top-1.5 left-2 text-[7.5px] uppercase tracking-wider text-muted-foreground/70">R</span>
                  <span className="absolute top-1.5 right-2 text-[7.5px] uppercase tracking-wider text-muted-foreground/70">L</span>
                </div>
                <div className="rounded border border-primary/40 bg-primary/5 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-wider text-primary">
                    AI · imaging evidence attached
                  </p>
                </div>
              </motion.div>
            )}

            {/* Stage 3–4: clinical note draft */}
            {stage >= 3 && (
              <motion.div
                key="note"
                initial={{ opacity: 0, y: reduce ? 0 : 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease }}
                className="relative rounded border border-border bg-foreground/[0.025] p-3 space-y-2"
              >
                <p className="text-[8.5px] uppercase tracking-wider text-muted-foreground">
                  Clinical note draft
                </p>
                <ul className="space-y-1.5 text-foreground/85 text-[10px] leading-snug">
                  <li className="flex gap-2"><span className="text-primary mt-0.5">·</span><span>EHR summary: ED arrival, hypotension, lactate 3.4</span></li>
                  <li className="flex gap-2"><span className="text-primary mt-0.5">·</span><span>Imaging evidence: RLL opacity</span></li>
                  <li className="flex gap-2"><span className="text-primary mt-0.5">·</span><span>Knowledge reference: sepsis pathway review</span></li>
                  <li className="flex gap-2"><span className="text-primary mt-0.5">·</span><span>Care team notification recommended</span></li>
                </ul>
                {stage >= 4 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, ease, delay: 0.2 }}
                    className="rounded border border-primary/40 bg-primary/5 px-2 py-1.5"
                  >
                    <p className="text-[9px] uppercase tracking-wider text-primary">
                      Routed to care team
                    </p>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* EHR + Assist column */}
        <div className="grid grid-cols-2 md:contents">
          {/* EHR patient summary (top right) */}
          <div className="md:col-span-3 md:row-span-1 border-r md:border-r-0 md:border-b border-border p-3">
            <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground mb-2">
              EHR · Patient
            </p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, ease, delay: 0.4 }}
              className="space-y-1.5"
            >
              <p className="text-foreground/85 font-medium truncate">S. Reinhardt · 41 F</p>
              <div className="space-y-1 text-muted-foreground">
                <div className="flex justify-between gap-2"><span>Context</span><span className="text-foreground/70 truncate">ED arrival</span></div>
                <div className="flex justify-between gap-2"><span>Vitals</span><span className="text-foreground/70 truncate">Hypotension</span></div>
                <div className="flex justify-between gap-2"><span>Labs</span><span className="text-foreground/70 truncate">Lactate 3.4</span></div>
                <div className="flex justify-between gap-2"><span>Imaging</span><span className="text-foreground/70 truncate">RLL opacity</span></div>
              </div>
            </motion.div>
          </div>

          {/* AI workflow support (bottom right) */}
          <div className="md:col-span-3 md:row-span-1 p-3 flex flex-col min-h-[140px] md:min-h-0 overflow-hidden">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                AI workflow support
              </p>
              {/* Activity indicator during Assist */}
              <AnimatePresence>
                {stage === 2 && !reduce && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-end gap-0.5 h-3"
                    aria-label="AI assist active"
                  >
                    {[0, 1, 2].map((i) => (
                      <motion.span
                        key={i}
                        className="w-0.5 bg-primary rounded-full"
                        animate={{ height: ["20%", "100%", "40%", "80%", "30%"] }}
                        transition={{ duration: 0.9, ease: "easeInOut", repeat: Infinity, delay: i * 0.12 }}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex-1 min-h-0 flex flex-col justify-start gap-1">
              {/* Assist bubble */}
              <AnimatePresence mode="wait">
                {stage >= 2 && stage < 3 && (
                  <motion.div
                    key="assist"
                    initial={{ opacity: 0, y: reduce ? 0 : 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.4, ease }}
                    className="rounded-md border border-border bg-card px-2 py-1.5 text-foreground/85 leading-snug text-[9px] md:text-[10px] line-clamp-5 md:line-clamp-none"
                  >
                    <span className="text-primary text-[8px] uppercase tracking-wider mr-1">Support</span>
                    {dictation.slice(0, typedChars)}
                    {typedChars < dictation.length && (
                      <span className="inline-block w-[1px] h-[8px] ml-[1px] bg-primary align-middle animate-pulse" />
                    )}
                  </motion.div>
                )}

                {/* Structured documentation */}
                {stage >= 3 && (
                  <motion.div
                    key="document"
                    initial={{ opacity: 0, y: reduce ? 0 : 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease }}
                    className="rounded-md border border-border bg-card px-2 py-1.5 space-y-1 text-[8.5px] md:text-[9px] leading-tight"
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="text-muted-foreground uppercase text-[7.5px] tracking-wider w-12 shrink-0">Summary</span>
                      <span className="text-foreground/85 flex-1">Hypotension · lactate 3.4 · RLL opacity</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-muted-foreground uppercase text-[7.5px] tracking-wider w-12 shrink-0">Evidence</span>
                      <span className="text-foreground/85 flex-1">RLL opacity on CXR</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-muted-foreground uppercase text-[7.5px] tracking-wider w-12 shrink-0">Knowledge</span>
                      <span className="text-foreground/85 flex-1">Sepsis pathway review</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-muted-foreground uppercase text-[7.5px] tracking-wider w-12 shrink-0">Next</span>
                      <span className="text-foreground/85 flex-1">Notify care team</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-muted-foreground uppercase text-[7.5px] tracking-wider w-12 shrink-0">Codes</span>
                      <span className="text-foreground/85 flex-1 flex flex-wrap gap-1">
                        <span className="rounded-sm border border-border bg-background px-1 py-px text-[8px] uppercase tracking-wider text-foreground/75 whitespace-nowrap">ICD-10 · J18.9</span>
                        <span className="rounded-sm border border-border bg-background px-1 py-px text-[8px] uppercase tracking-wider text-foreground/75 whitespace-nowrap">CPT · 71045</span>
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Routing chips */}
              <AnimatePresence>
                {stage >= 4 && (
                  <motion.div
                    key="route"
                    initial={{ opacity: 0, y: reduce ? 0 : 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease }}
                    className="flex flex-wrap items-center gap-1 pt-0.5"
                  >
                    <span className="rounded-full border border-border bg-background px-1.5 py-[1px] text-[7.5px] md:text-[8px] uppercase tracking-wider text-foreground/75 whitespace-nowrap">
                      EHR summarized
                    </span>
                    <span className="rounded-full border border-border bg-background px-1.5 py-[1px] text-[7.5px] md:text-[8px] uppercase tracking-wider text-foreground/75 whitespace-nowrap">
                      Imaging linked
                    </span>
                    <span className="rounded-full border border-border bg-background px-1.5 py-[1px] text-[7.5px] md:text-[8px] uppercase tracking-wider text-foreground/75 whitespace-nowrap">
                      Knowledge surfaced
                    </span>
                    <span className="rounded-full border border-border bg-background px-1.5 py-[1px] text-[7.5px] md:text-[8px] uppercase tracking-wider text-foreground/75 whitespace-nowrap">
                      Coding suggested
                    </span>
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4, ease, delay: 0.4 }}
                      className="rounded-full border border-primary/50 bg-primary/10 px-1.5 py-[1px] text-[7.5px] md:text-[8px] uppercase tracking-wider text-primary whitespace-nowrap"
                    >
                      Care team notified ✓
                    </motion.span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </ProductMockup>
  );
};

export default DeployMockup;
